/**
 * CommitmentDetail — Detail-Seite einer Vereinbarung mit Lifecycle-Aktionen,
 * UniversalChatter, Audit-Log + Aufgaben-Modal.
 *
 * @page /commitments/:id (auch /commitments/new als Neuanlage)
 * @api /api/commitments/:id, /api/commitments/:id/audit-log, /api/commitments/:id/submit-internal,
 *      /approve-internal, /reject-internal, /withdraw, /terminate, /activate-admin, POST /api/commitments
 * @i18n commitment.*
 * @since v2.408.0 / #930
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Send, CheckCircle2, XCircle, Loader2, History,
  Trash2, RotateCcw, Lock, AlertTriangle, FileText, EyeOff, Info, User,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import UniversalChatter from '../components/UniversalChatter';
import { StatusBadge, fmtDate } from './Commitments';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    let data;
    try { data = await r.json(); } catch { throw new Error(`HTTP ${r.status}`); }
    if (!r.ok) throw new Error(data.message || data.error || `HTTP ${r.status}`);
    return data;
  });
}

const KATEGORIE_OPTIONS = ['preis', 'service', 'sla', 'sonstige'];
const QUELLE_OPTIONS = ['call', 'email', 'meeting', 'manuell'];

function ActionConfirmModal({ open, title, body, onClose, onConfirm, requireGrund = true, busy = false, t }) {
  const [grund, setGrund] = useState('');
  useEffect(() => { if (open) setGrund(''); }, [open]);
  if (!open) return null;
  const canConfirm = !requireGrund || (grund.trim().length >= 3);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-card border border-line p-5 w-full max-w-md">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{title}</h3>
        <p className="text-sm text-[var(--text-secondary)] mb-3">{body}</p>
        {requireGrund && (
          <textarea
            className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm mb-3"
            rows={3}
            placeholder={t('commitment.grund_placeholder')}
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
          />
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-lg border border-line text-sm">{t('commitment.cancel')}</button>
          <button
            onClick={() => onConfirm(grund)}
            disabled={!canConfirm || busy}
            className="px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('commitment.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditSlideover({ open, onClose, items, t }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1" onClick={onClose} />
      <div className="w-[420px] max-w-full bg-white border-l border-line p-5 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">{t('commitment.audit.title')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-inset)]"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          {(items || []).map((it) => (
            <div key={it.id} className="border-l-2 border-[#338edc] pl-3 py-1">
              <div className="text-xs text-[var(--text-tertiary)]">
                {fmtDate(it.ausgefuehrt_am)} — {it.ausgefuehrt_von_name || 'System'}
              </div>
              <div className="text-sm text-[var(--text-primary)] font-medium">{t(`commitment.audit.aktion.${it.aktion}`) || it.aktion}</div>
              {it.begruendung && <div className="text-xs text-[var(--text-secondary)] mt-0.5">{it.begruendung}</div>}
            </div>
          ))}
          {!(items || []).length && (
            <div className="text-sm text-[var(--text-tertiary)]">{t('commitment.audit.empty')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommitmentDetail() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [data, setData] = useState(null);
  const [isApprover, setIsApprover] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditItems, setAuditItems] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [kunden, setKunden] = useState([]);

  const [form, setForm] = useState({
    kunde_id: '', titel: '', inhalt: '', bedingung: '', kategorie: 'sonstige',
    quelle_typ: 'manuell', quelle_datum: '', quelle_notiz: '',
    gueltig_ab: '', gueltig_bis: '',
  });

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const [c, audit] = await Promise.all([
        api(`/api/commitments/${id}`),
        api(`/api/commitments/${id}/audit-log`).catch(() => ({ items: [] })),
      ]);
      const row = c.commitment || c;
      setData(row);
      setIsApprover(!!c.is_approver);
      setAuditItems(audit.items || audit || []);
      setForm({
        kunde_id: row.kunde_id || '',
        titel: row.titel || '',
        inhalt: row.inhalt || '',
        bedingung: row.bedingung || '',
        kategorie: row.kategorie || 'sonstige',
        quelle_typ: row.quelle_typ || 'manuell',
        quelle_datum: row.quelle_datum ? String(row.quelle_datum).slice(0, 10) : '',
        quelle_notiz: row.quelle_notiz || '',
        gueltig_ab: row.gueltig_ab ? String(row.gueltig_ab).slice(0, 10) : '',
        gueltig_bis: row.gueltig_bis ? String(row.gueltig_bis).slice(0, 10) : '',
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isNew) {
      api('/api/kunden?aktiv=true').then((d) => setKunden(d.kunden || d || [])).catch(() => {});
    }
  }, [isNew]);

  const isDraft = data?.status === 'draft';
  const isPendingInternal = data?.status === 'pending_internal';
  const isPendingCustomer = data?.status === 'pending_customer';
  const isActive = data?.status === 'active';

  const onSave = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        ...form,
        kunde_id: form.kunde_id ? Number(form.kunde_id) : null,
        quelle_datum: form.quelle_datum || null,
        gueltig_ab: form.gueltig_ab || null,
        gueltig_bis: form.gueltig_bis || null,
      };
      if (isNew) {
        const res = await api('/api/commitments', { method: 'POST', body: payload });
        const newId = res.commitment?.id || res.id;
        navigate(`/commitments/${newId}`);
      } else {
        await api(`/api/commitments/${id}`, { method: 'PUT', body: payload });
        await load();
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }, [form, id, isNew, navigate, load]);

  const doAction = useCallback(async (endpoint, grund) => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/commitments/${id}/${endpoint}`, { method: 'POST', body: { grund } });
      setConfirmAction(null);
      await load();
    } catch (e) {
      setErr(e.message);
      setConfirmAction(null);
    } finally {
      setBusy(false);
    }
  }, [id, load]);

  if (loading) {
    return <div className="px-6 py-12 text-center text-[var(--text-tertiary)]"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />{t('commitment.loading')}</div>;
  }

  if (!isNew && !data) {
    return (
      <div className="px-6 py-12 space-y-4">
        <Link to="/commitments" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4">
          <ArrowLeft className="w-4 h-4" /> {t('commitment.back')}
        </Link>
        <div className="bg-rose-50 border border-rose-200 rounded-card p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
          <p className="text-sm text-rose-700">{err || t('commitment.not_found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/commitments" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft className="w-4 h-4" /> {t('commitment.back')}
        </Link>
        {!isNew && data && (
          <div className="flex items-center gap-2">
            <StatusBadge status={data.status} t={t} />
            {data.flowoms_bearbeiter_name && (
              <span
                title={t('commitment.flowoms_bearbeiter.label')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-line bg-[var(--bg-inset)] text-[var(--text-secondary)]"
              >
                <User className="w-3 h-3" />
                {data.flowoms_bearbeiter_name}
                {data.flowoms_bearbeiter_email && (
                  <span className="text-[var(--text-tertiary)]">({data.flowoms_bearbeiter_email})</span>
                )}
              </span>
            )}
            <button onClick={() => setAuditOpen(true)} className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              <History className="w-4 h-4" /> {t('commitment.audit.button')}
            </button>
          </div>
        )}
      </div>

      {!isNew && (isDraft || isPendingInternal) && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 flex items-start gap-3">
          <EyeOff size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <div className="font-medium">{t('commitment.visibility.hint_title')}</div>
            <div className="text-xs mt-0.5">
              {isDraft ? t('commitment.visibility.draft_hint') : t('commitment.visibility.pending_internal_hint')}
            </div>
          </div>
        </div>
      )}

      <div className="bg-surface-card border border-line rounded-card p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            {isNew ? (
              <h1 className="text-xl font-semibold text-[var(--text-primary)]">{t('commitment.new')}</h1>
            ) : (
              <>
                <div className="text-xs text-[var(--text-tertiary)] font-mono mb-0.5">{data?.commitment_nr || '—'}</div>
                <h1 className="text-xl font-semibold text-[var(--text-primary)]">{data?.titel || '—'}</h1>
                <div className="text-sm text-[var(--text-secondary)]">{data?.kunde_name || '—'}</div>
              </>
            )}
          </div>
        </div>

        {err && <div className="px-3 py-2 mb-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{err}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isNew && (
            <div className="md:col-span-2">
              <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.kunde')} *</label>
              <select
                value={form.kunde_id}
                onChange={(e) => setForm((f) => ({ ...f, kunde_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm"
              >
                <option value="">—</option>
                {kunden.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.titel')} *</label>
            <input
              value={form.titel}
              onChange={(e) => setForm((f) => ({ ...f, titel: e.target.value }))}
              disabled={!isNew && !isDraft}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.inhalt')} *</label>
            <textarea
              value={form.inhalt}
              onChange={(e) => setForm((f) => ({ ...f, inhalt: e.target.value }))}
              disabled={!isNew && !isDraft}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.bedingung')}</label>
            <textarea
              value={form.bedingung}
              onChange={(e) => setForm((f) => ({ ...f, bedingung: e.target.value }))}
              disabled={!isNew && !isDraft}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.kategorie')}</label>
            <select
              value={form.kategorie}
              onChange={(e) => setForm((f) => ({ ...f, kategorie: e.target.value }))}
              disabled={!isNew && !isDraft}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            >
              {KATEGORIE_OPTIONS.map((k) => <option key={k} value={k}>{t(`commitment.kategorie.${k}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.quelle_typ')}</label>
            <select
              value={form.quelle_typ}
              onChange={(e) => setForm((f) => ({ ...f, quelle_typ: e.target.value }))}
              disabled={!isNew && !isDraft}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            >
              {QUELLE_OPTIONS.map((q) => <option key={q} value={q}>{t(`commitment.quelle.${q}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.quelle_datum')}</label>
            <input
              type="date"
              value={form.quelle_datum}
              onChange={(e) => setForm((f) => ({ ...f, quelle_datum: e.target.value }))}
              disabled={!isNew && !isDraft}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.gueltig_ab')}</label>
            <input
              type="date"
              value={form.gueltig_ab}
              onChange={(e) => setForm((f) => ({ ...f, gueltig_ab: e.target.value }))}
              disabled={!isNew && !isDraft}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.gueltig_bis')}</label>
            <input
              type="date"
              value={form.gueltig_bis}
              onChange={(e) => setForm((f) => ({ ...f, gueltig_bis: e.target.value }))}
              disabled={!isNew && !isDraft}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('commitment.field.quelle_notiz')}</label>
            <textarea
              value={form.quelle_notiz}
              onChange={(e) => setForm((f) => ({ ...f, quelle_notiz: e.target.value }))}
              disabled={!isNew && !isDraft}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm disabled:opacity-60"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-5">
          {(isNew || isDraft) && (
            <button onClick={onSave} disabled={busy || !form.titel || !form.inhalt || !form.kunde_id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm disabled:opacity-50">
              <Save className="w-4 h-4" /> {t('commitment.action.save')}
            </button>
          )}
          {!isNew && isDraft && (
            <>
              <button onClick={() => setConfirmAction({ ep: 'submit-internal', title: t('commitment.action.submit_internal'), body: t('commitment.confirm.submit_internal'), require: false })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-line text-sm">
                <Send className="w-4 h-4" /> {t('commitment.action.submit_internal')}
              </button>
              <button onClick={() => setConfirmAction({ ep: '__delete', title: t('commitment.action.delete'), body: t('commitment.confirm.delete'), require: true })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 text-sm">
                <Trash2 className="w-4 h-4" /> {t('commitment.action.delete')}
              </button>
            </>
          )}
          {!isNew && isPendingInternal && isApprover && (
            <>
              <button onClick={() => setConfirmAction({ ep: 'approve-internal', title: t('commitment.action.approve'), body: t('commitment.confirm.approve_internal'), require: false })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm">
                <CheckCircle2 className="w-4 h-4" /> {t('commitment.action.approve')}
              </button>
              <button onClick={() => setConfirmAction({ ep: 'reject-internal', title: t('commitment.action.reject'), body: t('commitment.confirm.reject_internal'), require: true })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 text-sm">
                <XCircle className="w-4 h-4" /> {t('commitment.action.reject')}
              </button>
            </>
          )}
          {!isNew && isPendingInternal && (
            <button onClick={() => setConfirmAction({ ep: 'withdraw', title: t('commitment.action.withdraw'), body: t('commitment.confirm.withdraw'), require: true })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-line text-sm">
              <RotateCcw className="w-4 h-4" /> {t('commitment.action.withdraw')}
            </button>
          )}
          {!isNew && isPendingInternal && !isApprover && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--bg-inset)] border border-line text-sm text-[var(--text-secondary)]">
              <Info className="w-4 h-4 text-[var(--text-tertiary)]" /> {t('commitment.visibility.waiting_for_approver')}
            </div>
          )}
          {!isNew && isPendingCustomer && (
            <>
              <button onClick={() => setConfirmAction({ ep: 'activate-admin', title: t('commitment.action.activate_admin'), body: t('commitment.confirm.activate_admin'), require: true })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm">
                <Lock className="w-4 h-4" /> {t('commitment.action.activate_admin')}
              </button>
              <button onClick={() => setConfirmAction({ ep: 'withdraw', title: t('commitment.action.withdraw'), body: t('commitment.confirm.withdraw'), require: true })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-line text-sm">
                <RotateCcw className="w-4 h-4" /> {t('commitment.action.withdraw')}
              </button>
            </>
          )}
          {!isNew && isActive && (
            <button onClick={() => setConfirmAction({ ep: 'terminate', title: t('commitment.action.terminate'), body: t('commitment.confirm.terminate'), require: true })} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 text-sm">
              <XCircle className="w-4 h-4" /> {t('commitment.action.terminate')}
            </button>
          )}
        </div>
      </div>

      {!isNew && (
        <div className="bg-surface-card border border-line rounded-card p-5">
          <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" /> {t('commitment.chatter.title')}
          </h2>
          <UniversalChatter entityType="commitment" entityId={parseInt(id, 10)} />
        </div>
      )}

      <ActionConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title}
        body={confirmAction?.body}
        requireGrund={confirmAction?.require !== false}
        busy={busy}
        onClose={() => setConfirmAction(null)}
        onConfirm={(grund) => {
          if (!confirmAction) return;
          if (confirmAction.ep === '__delete') {
            (async () => {
              setBusy(true);
              try { await api(`/api/commitments/${id}`, { method: 'DELETE', body: { grund } }); navigate('/commitments'); }
              catch (e) { setErr(e.message); setConfirmAction(null); }
              finally { setBusy(false); }
            })();
          } else {
            doAction(confirmAction.ep, grund);
          }
        }}
        t={t}
      />
      <AuditSlideover open={auditOpen} onClose={() => setAuditOpen(false)} items={auditItems} t={t} />
    </div>
  );
}
